import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  Icon,
  Link,
  List,
  ListItem,
  ListItemIcon,
  Typography,
  useMediaQuery,
} from '@material-ui/core';
import { Link as RouterLink } from 'react-router-dom';
import { AuthContext, PageContext } from '../../contexts';
import { usePrimaryNavStyles } from './styles';
import { Tooltip } from '..';
import {
  CustomersIcon,
  HomeIcon,
  HelpIcon,
  LeadsIcon,
  ProfileIcon,
  SettingsIcon,
  SignoutIcon,
  OpportunityIcon,
  CampaignsIcon,
} from '../../assets/icons';
import config from '../../config';
import { ROLE_HEADQUARTERS } from '../../data';
import { toTitleCase } from '../../utils';
import { canManageProjectAccess, PROJECT_ACCESS_PATH } from '../../pages/Settings/ProjectAccess/access'; // TEMP Vietnam project access

function ListItemLink(properties) {
  return <ListItem button component="a" {...properties} />;
}

const PrimaryNav = (props) => {
  const classes = usePrimaryNavStyles();
  const { t } = useTranslation();
  const authContext = useContext(AuthContext);
  const { user, apps, country } = authContext.state;
  const { signOut } = authContext.actions;
  const pageContext = useContext(PageContext);
  const { lastReleaseNoteVersionSeen } = pageContext.state;

  const { selected, onSelect } = props;

  const handleSignOut = () => {
    signOut();
  };

  const items = [
    {
      name: '',
      title: toTitleCase(t('entity.home', 'home')),
      to: '/',
      icon: HomeIcon,
    },
    {
      name: 'leads',
      title: toTitleCase(t('entity.leads', 'leads')),
      to: '/leads?page=1',
      icon: LeadsIcon,
    },
    {
      name: 'customers',
      title: toTitleCase(t('entity.customers', 'customers')),
      to: '/customers?page=1',
      icon: CustomersIcon,
    },
    {
      name: 'opportunities',
      title: toTitleCase(t('entity.opportunities', 'opportunities')),
      to: '/opportunities?page=1',
      icon: OpportunityIcon,
    },
    {
      name: 'campaigns',
      title: toTitleCase(t('entity.campaigns', 'campaigns')),
      to: '/campaigns?page=1',
      icon: CampaignsIcon,
    },
    // LMS-2843
    // {
    //   name: 'chats',
    //   title: 'Chats',
    //   to: '/chats',
    //   icon: CommentIcon,
    // },
  ];

  // LMS-878: Hide settings from Staff
  if (user.role && user.role === ROLE_HEADQUARTERS) {
    const settingsTo = country.features.settingsDefaultPath || '/settings/fields';
    items.push({
      name: 'settings',
      title: toTitleCase(t('component.text.settings', 'settings')),
      to: settingsTo,
      icon: SettingsIcon,
    });
  } else if (canManageProjectAccess(user.role, country.features)) {
    // TEMP Vietnam project access: Sales Admin's only settings page.
    items.push({
      name: 'settings',
      title: toTitleCase(t('component.text.settings', 'settings')),
      to: PROJECT_ACCESS_PATH,
      icon: SettingsIcon,
    });
  }

  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  if (isMobile) {
    const newReleaseNoteAvailable = (
      !lastReleaseNoteVersionSeen
      || (lastReleaseNoteVersionSeen && lastReleaseNoteVersionSeen !== config.app.version)
    );
    items.push({
      name: 'help',
      title: t('component.label.help', 'Help'),
      to: '/faq',
      icon: HelpIcon,
      haveBadge: newReleaseNoteAvailable,
    });
  }

  // Hide nav items based on country features
  const filteredItems = items.filter((item) => {
    if (item.name === '' && country.features.initialPath !== '/') return false;
    if (item.name === 'leads' && !country.features.navLeads) return false;
    if (item.name === 'campaigns' && !country.features.navCampaigns) return false;
    return true;
  });

  const profileURL = apps.showroom && apps.showroom.webURL ? apps.showroom.webURL : '';
  return (
    <nav className={classes.root}>
      <List component="nav" className={classes.list}>
        {
          filteredItems.map((item) => (
            <ListItemLink
              button
              key={item.name}
              name={item.name}
              className={classes.item}
              component={RouterLink}
              to={item.to}
              selected={selected === item.name}
              onClick={onSelect}
            >
              <Badge
                color="error"
                variant="dot"
                className={classes.badge}
                invisible={!item.haveBadge}
              >
                <ListItemIcon className={classes.icon}>
                  <Tooltip title={item.title} placement="right">
                    <Icon>
                      <img alt={item.title} src={item.icon} />
                    </Icon>
                  </Tooltip>
                </ListItemIcon>
              </Badge>
              <Typography variant="h6">{ item.title }</Typography>
            </ListItemLink>
          ))
        }
      </List>
      <div className={classes.profile}>
        <Icon className={classes.profileIcon}>
          <img alt="User" src={ProfileIcon} />
        </Icon>
        <div className={classes.profileName}>
          <Typography variant="subtitle2">{user.name}</Typography>
          <Link variant="caption" href={`${profileURL}/profile/`}>
            {t('appnav.button.helpview_profile', 'View Profile')}
          </Link>
        </div>
        <Icon className={classes.profileIcon} onClick={handleSignOut}>
          <img alt="Signout" src={SignoutIcon} />
        </Icon>
      </div>
    </nav>
  );
};

PrimaryNav.propTypes = {
  selected: PropTypes.string,
  onSelect: PropTypes.func,
};

PrimaryNav.defaultProps = {
  selected: '',
  onSelect: () => {},
};

export default PrimaryNav;
